import React from 'react';
import PropTypes from 'prop-types';
import { QRCodeSVG } from 'qrcode.react';
import { useApiQuery } from '../../../../../../api/hooks';
import { PersonIcon, CoinIcon } from '../../../../model/icons';
import {
    formatDt, participantName, qrCodeUrl,
    TASK_USE_STATUS, DUEL_STATUS, CHALLENGE_STATUS, EXTERNAL_REASON_LABEL
} from './participantsUtils';

// Плитка введённого кода (.sp-codetile). Для QUESTION: «сканировал: <код>» + разделитель + QR фактически
// выпавшего вопроса (грузим по answeredQuestionCodeId → find-by-id) с бейджем результата; клик → вопрос и ответ.
// Остальные типы — код/вопрос/медиа как есть.
// React.memo: тайлы с QR не перерисовываются от стейта модалки участника (табы, чипы, балансы).
export const CodeEventRow = React.memo(function CodeEventRow({ event, onOpenAnswered, qrBaseUrl }) {
    const { codeType, codeValue, reward, answeredCorrect, question, playerAnswer, activatedAt, mediaFiles, usedBy } = event;
    const isQuestion = codeType === 'QUESTION';
    const answeredId = event.answeredQuestionCodeId;

    const { data: answeredCode } = useApiQuery(
        ['neighbor', 'question-code', answeredId],
        `/api/neighbor/admin/question-codes/find-by-id?id=${answeredId}`,
        { enabled: isQuestion && answeredId != null }
    );

    if (isQuestion) {
        let answerBadge;
        if (answeredCorrect === true) answerBadge = <span className="sp-badge done">Ответ верный</span>;
        else if (answeredCorrect === false) answerBadge = <span className="sp-badge lose">Ответ неверный</span>;
        else answerBadge = <span className="sp-badge wait">Не отвечено</span>;

        const qr = answeredCode?.value ? qrCodeUrl(qrBaseUrl, 'questionCode', answeredCode.value) : null;
        const open = answeredId != null && onOpenAnswered ? () => onOpenAnswered(answeredId) : null;

        return (
            <div className="sp-codetile">
                {usedBy && <div className="er-u"><PersonIcon />{participantName(usedBy)}</div>}
                <div className="er-badges">{answerBadge}</div>
                <div className="sp-scan">сканировал: <span className="mono">{codeValue}</span></div>
                <div className="sp-tile-div" />
                {qr ? (
                    <button type="button" className="sp-qrbtn" onClick={open || undefined} disabled={!open}>
                        <QRCodeSVG value={qr} size={96} level="M" />
                    </button>
                ) : null}
                <div className="val mono">
                    {open ? (
                        <button type="button" className="er-qlink" onClick={open}>{answeredCode?.value || 'открыть вопрос'}</button>
                    ) : (answeredCode?.value || '—')}
                </div>
                {playerAnswer && <div className="sp-panswer"><span className="l">Ответ игрока</span><span className="v">{playerAnswer}</span></div>}
                <div className="foot">
                    <span className="amt">{reward} <CoinIcon /></span>
                    <span className="when">{formatDt(activatedAt)}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="sp-codetile">
            {mediaFiles?.length > 0 && (
                <div className="photo">
                    <img src={mediaFiles[0].mediaFileUrl} alt="" />
                    {mediaFiles.length > 1 && <span className="cnt">+{mediaFiles.length - 1}</span>}
                </div>
            )}
            <div className="val mono">{codeValue}</div>
            {question && <div className="q">{question}</div>}
            <div className="foot">
                <span className="amt">{reward} <CoinIcon /></span>
                <span className="when">{formatDt(activatedAt)}</span>
            </div>
        </div>
    );
});

CodeEventRow.propTypes = {
    event: PropTypes.shape({
        codeType: PropTypes.string,
        codeValue: PropTypes.string,
        reward: PropTypes.number,
        answeredCorrect: PropTypes.bool,
        question: PropTypes.string,
        answeredQuestionCodeId: PropTypes.number,
        playerAnswer: PropTypes.string,
        activatedAt: PropTypes.string,
        mediaFiles: PropTypes.array,
        usedBy: PropTypes.object
    }).isRequired,
    onOpenAnswered: PropTypes.func,
    qrBaseUrl: PropTypes.string
};

CodeEventRow.defaultProps = { onOpenAnswered: undefined, qrBaseUrl: null };

// Строка операции с заданием (.sp-erow): роль, задание, описание, статус (обычный/дуэль/челлендж/подарок дня), контрагент.
export function TaskOperationRow({ op }) {
    const { role, task, taskUseStatus, duelStatus, challengeStatus, dailyGiftType, buyTime, counterparty } = op;

    const { data: taskDetail } = useApiQuery(
        ['neighbor', 'task-detail', task?.id],
        `/api/neighbor/admin/tasks/find-by-id?id=${task?.id}`,
        { enabled: !!task?.id }
    );

    let statusBadge = null;
    if (duelStatus && DUEL_STATUS[duelStatus]) {
        const s = DUEL_STATUS[duelStatus];
        statusBadge = <span className={`sp-badge ${s.tone}`}>{s.label}</span>;
    } else if (challengeStatus && CHALLENGE_STATUS[challengeStatus]) {
        const s = CHALLENGE_STATUS[challengeStatus];
        statusBadge = <span className={`sp-badge ${s.tone}`}>{s.label}</span>;
    } else if (taskUseStatus === 'ACTIVE') {
        // «Активно» осмысленно только для заданий с ограничением по времени (useTime).
        if (taskDetail?.useTime) statusBadge = <span className="sp-badge wait">Активно</span>;
    } else if (taskUseStatus && TASK_USE_STATUS[taskUseStatus]) {
        const s = TASK_USE_STATUS[taskUseStatus];
        statusBadge = <span className={`sp-badge ${s.tone}`}>{s.label}</span>;
    }

    return (
        <div className="sp-erow">
            <div className="er-l">
                <div className="er-badges">
                    {role === 'OWNER' ? <span className="sp-badge clay">Покупка</span> : <span className="sp-badge wait">Назначено</span>}
                    {task?.isChallenge && <span className="sp-badge wait">Челлендж</span>}
                    {dailyGiftType && <span className="sp-badge info">Подарок дня</span>}
                    {statusBadge}
                </div>
                <div className="er-t">{task?.title || '—'}</div>
                {counterparty && <div className="er-u"><PersonIcon />{participantName(counterparty)}</div>}
                {taskDetail?.description && <div className="er-desc">{taskDetail.description}</div>}
                {taskDetail?.conditions && <div className="er-desc cond">Условия: {taskDetail.conditions}</div>}
            </div>
            <div className="er-r">
                <div className="er-amt">{task?.cost ?? 0} <CoinIcon /></div>
                <div className="er-when">{formatDt(buyTime)}</div>
            </div>
        </div>
    );
}

TaskOperationRow.propTypes = {
    op: PropTypes.shape({
        role: PropTypes.string,
        task: PropTypes.shape({
            id: PropTypes.number,
            title: PropTypes.string,
            cost: PropTypes.number,
            isChallenge: PropTypes.bool
        }),
        taskUseStatus: PropTypes.string,
        duelStatus: PropTypes.string,
        challengeStatus: PropTypes.string,
        dailyGiftType: PropTypes.string,
        buyTime: PropTypes.string,
        counterparty: PropTypes.object
    }).isRequired
};

// Строка перевода баллов (.sp-erow): направление, сумма (+/−), контрагент, время.
export function CoinTransferRow({ transfer }) {
    const { direction, amount, counterparty, createdAt } = transfer;
    const sent = direction === 'SENT';

    return (
        <div className="sp-erow">
            <div className="er-l">
                <div className="er-t">{sent ? 'Отправлено' : 'Получено'}</div>
                {counterparty && <div className="er-u"><PersonIcon />{participantName(counterparty)}</div>}
            </div>
            <div className="er-r">
                <div className={`er-amt ${sent ? 'lose' : 'win'}`}>{sent ? '−' : '+'}{amount} <CoinIcon /></div>
                <div className="er-when">{formatDt(createdAt)}</div>
            </div>
        </div>
    );
}

CoinTransferRow.propTypes = {
    transfer: PropTypes.shape({
        direction: PropTypes.string,
        amount: PropTypes.number,
        counterparty: PropTypes.object,
        createdAt: PropTypes.string
    }).isRequired
};

// Строка внешнего начисления (.sp-erow): источник (другой сервис, напр. Ставки), сумма, время.
export function ExternalAccrualRow({ accrual }) {
    const { amount, reason, createdAt } = accrual;

    return (
        <div className="sp-erow">
            <div className="er-l">
                <div className="er-t">{EXTERNAL_REASON_LABEL[reason] || reason}</div>
            </div>
            <div className="er-r">
                <div className="er-amt win">+{amount} <CoinIcon /></div>
                <div className="er-when">{formatDt(createdAt)}</div>
            </div>
        </div>
    );
}

ExternalAccrualRow.propTypes = {
    accrual: PropTypes.shape({
        amount: PropTypes.number,
        reason: PropTypes.string,
        createdAt: PropTypes.string
    }).isRequired
};
